<template>
  <div
    v-if="showSpecificationArea"
    :id="pageNavigationIds.SPECIFICATION"
    class="specification-area"
  >
    <div class="specification-area__content">
      <DecorationHead text="商品仕様" :heading-level="2" />
      <div class="specification-area__body">
        <div v-if="showBestBeforeDate" class="specification-area__item">
          <HeadingFour>賞味期限</HeadingFour>
          <p class="specification-area__text">
            <TextWithLinks
              :text="foodAndBeverageDetail.bestBeforeDate"
              link-class="specification-area__link"
            />
          </p>
        </div>

        <div v-if="showExpirationDate" class="specification-area__item">
          <HeadingFour>消費期限</HeadingFour>
          <p class="specification-area__text">
            <TextWithLinks
              :text="foodAndBeverageDetail.expirationDate"
              link-class="specification-area__link"
            />
          </p>
        </div>

        <div v-if="showAllergyInformation" class="specification-area__item">
          <HeadingFour>アレルギー食材</HeadingFour>
          <p v-if="showAllergenContains" class="specification-area__text">
            <span>以下の食材が含まれております。</span>
          </p>
          <div v-if="showAllergens" class="specification-area__allergens">
            <span
              v-for="allergen in allergenLabels"
              :key="allergen"
              class="specification-area__allergen"
            >
              {{ allergen }}
            </span>
          </div>
          <p v-if="showAllergenNotContains" class="specification-area__text">
            <span>含まれておりません。</span>
          </p>
          <p v-if="showAllergenPossibility" class="specification-area__text">
            <span class="specification-area__allergen-possibility-text">
              商品ラベルの表示を必ずご確認いただくようお願いいたします。
            </span>
          </p>
          <div
            v-if="showAllergyGeneralCaution"
            class="specification-area__allergy-caution"
          >
            <TextWithLinks
              :text="foodAndBeverageDetail.allergyGeneralCaution"
              link-class="specification-area__link"
            />
          </div>
        </div>

        <div v-if="showIngredients" class="specification-area__item">
          <HeadingFour>原材料名</HeadingFour>
          <p class="specification-area__text">
            <TextWithLinks
              :text="foodAndBeverageDetail.ingredients"
              link-class="specification-area__link"
            />
          </p>
        </div>

        <div v-if="showSetContents" class="specification-area__item">
          <HeadingFour>セット内容</HeadingFour>
          <p class="specification-area__text">
            <TextWithLinks
              :text="setContents"
              link-class="specification-area__link"
            />
          </p>
        </div>

        <div v-if="showCapacitySizeWeight" class="specification-area__item">
          <HeadingFour>内容量・サイズ・重さなど</HeadingFour>
          <p class="specification-area__text">
            <TextWithLinks
              :text="capacitySizeWeight"
              link-class="specification-area__link"
            />
          </p>
        </div>

        <div v-if="showOtherSpecifications" class="specification-area__item">
          <HeadingFour>その他 仕様</HeadingFour>
          <p class="specification-area__text">
            <TextWithLinks
              :text="otherSpecifications"
              link-class="specification-area__link"
            />
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeadingFour from '../../atoms/headlines/HeadingFour.vue'
import TextWithLinks from '../../atoms/text/TextWithLinks.vue'
import DecorationHead from '../../atoms/decoration/DecorationHead.vue'
import { pageNavigationIds } from '../../../constants/pageNavigationConstants'

const ALLERGEN_TYPE = {
  YES: 'yes',
  NO: 'no',
  UNKNOWN: 'unknown',
  PENDING: 'pending'
}

export default {
  name: 'SpecificationArea',
  components: {
    HeadingFour,
    TextWithLinks,
    DecorationHead
  },
  props: {
    foodAndBeverageDetail: {
      type: Object,
      required: false,
      default: null
    },
    setContents: {
      type: String,
      required: false,
      default: ''
    },
    capacitySizeWeight: {
      type: String,
      required: false,
      default: ''
    },
    otherSpecifications: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      pageNavigationIds
    }
  },
  computed: {
    showBestBeforeDate() {
      return (
        this.foodAndBeverageDetail?.bestBeforeDate &&
        this.foodAndBeverageDetail.bestBeforeDate.trim() !== ''
      )
    },
    showExpirationDate() {
      return (
        this.foodAndBeverageDetail?.expirationDate &&
        this.foodAndBeverageDetail.expirationDate.trim() !== ''
      )
    },
    showAllergyInformation() {
      const detail = this.foodAndBeverageDetail
      if (!detail) return false

      const hasAllergenType = detail.allergenType && detail.allergenType !== ''
      const hasAllergyGeneralCaution =
        detail.allergyGeneralCaution &&
        detail.allergyGeneralCaution.trim() !== ''

      return hasAllergenType || hasAllergyGeneralCaution
    },
    showAllergenContains() {
      return this.foodAndBeverageDetail?.allergenType === ALLERGEN_TYPE.YES
    },
    showAllergens() {
      return (
        this.foodAndBeverageDetail?.allergenType === ALLERGEN_TYPE.YES &&
        this.foodAndBeverageDetail?.allergens?.length > 0
      )
    },
    allergenLabels() {
      return this.foodAndBeverageDetail?.allergens || []
    },
    showAllergenNotContains() {
      return this.foodAndBeverageDetail?.allergenType === ALLERGEN_TYPE.NO
    },
    showAllergenPossibility() {
      const type = this.foodAndBeverageDetail?.allergenType
      return (
        type === ALLERGEN_TYPE.YES ||
        type === ALLERGEN_TYPE.UNKNOWN ||
        type === ALLERGEN_TYPE.PENDING
      )
    },
    showAllergyGeneralCaution() {
      return (
        this.foodAndBeverageDetail?.allergyGeneralCaution &&
        this.foodAndBeverageDetail.allergyGeneralCaution.trim() !== ''
      )
    },
    showIngredients() {
      return (
        this.foodAndBeverageDetail?.ingredients &&
        this.foodAndBeverageDetail.ingredients.trim() !== ''
      )
    },
    showSetContents() {
      return this.setContents && this.setContents.trim() !== ''
    },
    showCapacitySizeWeight() {
      return this.capacitySizeWeight && this.capacitySizeWeight.trim() !== ''
    },
    showOtherSpecifications() {
      return this.otherSpecifications && this.otherSpecifications.trim() !== ''
    },
    showSpecificationArea() {
      return (
        this.showBestBeforeDate ||
        this.showExpirationDate ||
        this.showAllergyInformation ||
        this.showIngredients ||
        this.showSetContents ||
        this.showCapacitySizeWeight ||
        this.showOtherSpecifications
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.specification-area {
  margin: 16px auto 0 auto;
  background-color: $white;
  text-align: left;

  &__content {
    padding-bottom: 32px;
    margin: 0 auto;
    max-width: 768px;
  }

  &__body {
    width: auto;
    padding: 24px 24px 0 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media all and (max-width: ($base-contents-width - 1)) {
      padding: 24px 16px 0 16px;
    }
  }
}

.specification-area__item {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.specification-area__text {
  @extend %body;
  color: $black;
  white-space: pre-wrap;
  letter-spacing: 0.28px;
  line-height: 1.6;
}

.specification-area__link {
  color: $link;
  word-break: break-all;
}

.specification-area__allergens {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.specification-area__allergen {
  @extend %caption-bold;
  color: $black;
  display: inline-flex;
  padding: 8px;
  border-radius: 8px;
  background: $primary-lighten;
}

.specification-area__allergy-caution {
  color: $gray-1;
  background-color: $gray-3;
  border: 1px solid $gray-2;
  border-radius: 4px;
  padding: 16px;
  font-size: 1.4rem;
  line-height: 1.6;
  letter-spacing: 0.56px;
  white-space: pre-wrap;
}

.specification-area__allergen-possibility-text {
  white-space: normal;
}
</style>
